import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useOctaveWorker } from '../../hooks/useOctaveWorker';
import { usePythonWorker } from '../../hooks/usePythonWorker';
import { codeEditorApi } from '../../utils/api/CodeEditorApi';

// Helper function to render 3D plots
const render3DPlot = (plot, jsonData, idx, currentOutput, username = 'User') => {
  const width = 600;
  const height = 500;
  const padding = 60;

  // Extract plot number for metadata
  const plotNumber = plot.filename.match(/plot_(\d+)\.json/)?.[1];
  const titleArtifact = currentOutput.artifacts.find(a => a.filename === `plot_${plotNumber}_title.txt`);
  const xlabelArtifact = currentOutput.artifacts.find(a => a.filename === `plot_${plotNumber}_xlabel.txt`);
  const ylabelArtifact = currentOutput.artifacts.find(a => a.filename === `plot_${plotNumber}_ylabel.txt`);

  const title = titleArtifact ? new TextDecoder().decode(titleArtifact.data) : '';
  const xlabel = xlabelArtifact ? new TextDecoder().decode(xlabelArtifact.data) : '';
  const ylabel = ylabelArtifact ? new TextDecoder().decode(ylabelArtifact.data) : '';

  // Isometric projection function
  const projectIsometric = (x, y, z, xMin, xMax, yMin, yMax, zMin, zMax) => {
    // Normalize coordinates to 0-1 range
    const xNorm = (x - xMin) / (xMax - xMin || 1);
    const yNorm = (y - yMin) / (yMax - yMin || 1);
    const zNorm = (z - zMin) / (zMax - zMin || 1);

    // Scale to plot dimensions
    const plotWidth = width - 2 * padding;
    const plotHeight = height - 2 * padding;

    // Isometric projection angles (30 degrees)
    const angle = Math.PI / 6; // 30 degrees
    const px = padding + plotWidth * 0.5 + (xNorm - yNorm) * Math.cos(angle) * plotWidth * 0.4;
    const py = height - padding - plotHeight * 0.2 - zNorm * plotHeight * 0.6 - (xNorm + yNorm) * Math.sin(angle) * plotHeight * 0.3;

    return { px, py };
  };

  // Helper function to download SVG as PDF (same approach as ERD editor)
  const downloadAsPDF = (svgElement, filename) => {
    if (!svgElement) return;

    try {
      const today = new Date();
      const svgData = new XMLSerializer().serializeToString(svgElement);

      // Create HTML content for PDF printing (matching ERD editor style)
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${filename}</title>
          <style>
            body {
              margin: 0;
              padding: 20px;
              font-family: Arial, sans-serif;
              background: white;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            .header h1 {
              margin: 0 0 10px 0;
              font-size: 24px;
            }
            .header p {
              margin: 5px 0;
              color: #666;
            }
            .diagram-container {
              display: flex;
              justify-content: center;
              align-items: center;
              width: 100%;
              min-height: 400px;
              user-select: none;
              -webkit-user-select: none;
              -moz-user-select: none;
              -ms-user-select: none;
            }
            .diagram-container svg {
              max-width: 100%;
              height: auto;
              user-select: none;
              -webkit-user-select: none;
              -moz-user-select: none;
              -ms-user-select: none;
            }
            .diagram-container svg text {
              user-select: none;
              -webkit-user-select: none;
              -moz-user-select: none;
              -ms-user-select: none;
            }
            .footer {
              margin-top: 20px;
              text-align: center;
              font-size: 12px;
              color: #666;
              border-top: 1px solid #ccc;
              padding-top: 10px;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>MATLAB Plot</h1>
            <p>User: ${username} | Date: ${today.toLocaleDateString()}</p>
          </div>
          <div class="diagram-container">
            ${svgData}
          </div>
          <div class="footer">
            <p>Generated on ${today.toLocaleString()}</p>
          </div>
        </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      };

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  if (jsonData.type === 'plot3d') {
    // 3D line plot
    const { x, y, z } = jsonData;

    const xMin = Math.min(...x);
    const xMax = Math.max(...x);
    const yMin = Math.min(...y);
    const yMax = Math.max(...y);
    const zMin = Math.min(...z);
    const zMax = Math.max(...z);

    // Generate path
    const pathData = x.map((xVal, i) => {
      const { px, py } = projectIsometric(xVal, y[i], z[i], xMin, xMax, yMin, yMax, zMin, zMax);
      const command = i === 0 ? 'M' : 'L';
      return `${command} ${px} ${py}`;
    }).join(' ');

    return (
      <div key={idx} className="bg-white border rounded p-4 w-full overflow-hidden">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600">3D Plot</span>
          <button
            onClick={() => {
              const svgElement = document.getElementById(`plot-3d-svg-${idx}`);
              if (svgElement) {
                downloadAsPDF(svgElement, `plot_${idx + 1}_3d`);
              }
            }}
            className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Download PDF
          </button>
        </div>
        <div className="bg-gray-50 p-4 rounded w-full overflow-hidden" style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}>
          <svg
            id={`plot-3d-svg-${idx}`}
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="xMidYMid meet"
            className="w-full h-auto max-w-full"
            style={{ maxHeight: '500px', userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none', cursor: 'grab' }}
          >
            {/* Title */}
            {title && (
              <text x={width / 2} y={25} textAnchor="middle" fontSize="16" fontWeight="bold" fill="#1f2937">
                {title} (3D)
              </text>
            )}

            {/* 3D axes hint */}
            <text x={width / 2} y={height - 10} textAnchor="middle" fontSize="10" fill="#6b7280">
              3D plot (isometric projection)
            </text>

            {/* Plot line */}
            <path d={pathData} fill="none" stroke="#3b82f6" strokeWidth="2" />

            {/* Data points */}
            {x.map((xVal, i) => {
              const { px, py } = projectIsometric(xVal, y[i], z[i], xMin, xMax, yMin, yMax, zMin, zMax);
              return (
                <circle
                  key={i}
                  cx={px}
                  cy={py}
                  r="3"
                  fill="#3b82f6"
                  stroke="#1e40af"
                  strokeWidth="1"
                />
              );
            })}
          </svg>
        </div>
        <div className="mt-2 text-xs text-gray-500 bg-yellow-50 border border-yellow-200 rounded p-2">
          ℹ️ Note: 3D visualization is simplified. For full 3D interaction, consider using MATLAB desktop or exporting data.
        </div>
      </div>
    );
  } else if (jsonData.type === 'surf') {
    // Surface plot - simplified wireframe representation
    const { X, Y, Z, rows, cols } = jsonData;

    // Calculate bounds
    const xMin = Math.min(...X);
    const xMax = Math.max(...X);
    const yMin = Math.min(...Y);
    const yMax = Math.max(...Y);
    const zMin = Math.min(...Z);
    const zMax = Math.max(...Z);

    // Generate mesh lines
    const meshLines = [];

    // Row lines
    for (let i = 0; i < rows; i++) {
      const pathData = [];
      for (let j = 0; j < cols; j++) {
        const idx = i * cols + j;
        const { px, py } = projectIsometric(X[idx], Y[idx], Z[idx], xMin, xMax, yMin, yMax, zMin, zMax);
        pathData.push(`${j === 0 ? 'M' : 'L'} ${px} ${py}`);
      }
      meshLines.push(pathData.join(' '));
    }

    // Column lines
    for (let j = 0; j < cols; j++) {
      const pathData = [];
      for (let i = 0; i < rows; i++) {
        const idx = i * cols + j;
        const { px, py } = projectIsometric(X[idx], Y[idx], Z[idx], xMin, xMax, yMin, yMax, zMin, zMax);
        pathData.push(`${i === 0 ? 'M' : 'L'} ${px} ${py}`);
      }
      meshLines.push(pathData.join(' '));
    }

    return (
      <div key={idx} className="bg-white border rounded p-4 w-full overflow-hidden">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600">3D Surface Plot</span>
          <button
            onClick={() => {
              const svgElement = document.getElementById(`plot-surf-svg-${idx}`);
              if (svgElement) {
                downloadAsPDF(svgElement, `plot_${idx + 1}_surface`);
              }
            }}
            className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Download PDF
          </button>
        </div>
        <div className="bg-gray-50 p-4 rounded w-full overflow-hidden" style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}>
          <svg
            id={`plot-surf-svg-${idx}`}
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="xMidYMid meet"
            className="w-full h-auto max-w-full"
            style={{ maxHeight: '500px', userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none', cursor: 'grab' }}
          >
            {/* Title */}
            {title && (
              <text x={width / 2} y={25} textAnchor="middle" fontSize="16" fontWeight="bold" fill="#1f2937">
                {title} (3D Surface)
              </text>
            )}

            {/* 3D axes hint */}
            <text x={width / 2} y={height - 10} textAnchor="middle" fontSize="10" fill="#6b7280">
              3D surface plot ({rows}×{cols} grid, wireframe view)
            </text>

            {/* Mesh lines */}
            <g>
              {meshLines.map((path, i) => (
                <path
                  key={i}
                  d={path}
                  fill="none"
                  stroke={i < rows ? "#3b82f6" : "#60a5fa"}
                  strokeWidth="1"
                  opacity="0.6"
                />
              ))}
            </g>
          </svg>
        </div>
        <div className="mt-2 text-xs text-gray-500 bg-yellow-50 border border-yellow-200 rounded p-2">
          ℹ️ Note: 3D surface shown as wireframe. For full 3D interaction, consider using MATLAB desktop or exporting data.
        </div>
      </div>
    );
  }

  return null;
};

const OutputPane = forwardRef(({
  showOutput,
  onToggleOutput,
  activeFile,
  code,
  language,
  placement = 'bottom', // 'bottom' or 'right'
  executionHistory,
  setExecutionHistory,
  currentOutput,
  setCurrentOutput,
  classContext,
  username = 'User',
  onExecutionStateChange
}, ref) => {
  const [activeTab, setActiveTab] = useState('current'); // 'current' | 'history'

  const { isReady: octaveReady, isExecuting: isExecutingOctave, execute: executeOctave, cancelExecution: cancelOctave } = useOctaveWorker();
  const { isReady: pythonReady, isExecuting: isExecutingPython, execute: executePython, cancelExecution: cancelPython } = usePythonWorker();

  // Notify parent of execution state changes
  useEffect(() => {
    if (onExecutionStateChange) {
      onExecutionStateChange(isExecutingPython || isExecutingOctave);
    }
  }, [isExecutingPython, isExecutingOctave, onExecutionStateChange]);

  // Performance logging for worker initialization
  useEffect(() => {
    const startTime = performance.now();
    const hasLoggedRef = { current: false };
    console.log('[OutputPane] Workers initializing...');

    const checkReady = () => {
      if (octaveReady && pythonReady && !hasLoggedRef.current) {
        const loadTime = performance.now() - startTime;
        console.log(`[OutputPane] All workers ready in ${loadTime.toFixed(2)}ms`);
        hasLoggedRef.current = true;
        clearInterval(interval);
      }
    };

    // Check periodically (workers initialize asynchronously)
    const interval = setInterval(checkReady, 100);

    return () => {
      clearInterval(interval);
    };
  }, [octaveReady, pythonReady]);

  // Combined execution state
  const isExecuting = isExecutingOctave || isExecutingPython;

  // Function to send execution log to backend
  const sendExecutionLog = async (outputData, executionTimeMs) => {
    try {
      const now = new Date();
      const startedAt = new Date(now.getTime() - (executionTimeMs || 0));

      const logData = {
        code: code,
        filename: outputData.fileName,
        project_id: activeFile?.file?.project_id || activeFile?.project_id || null,
        execution_time: {
          started_at: startedAt.toISOString(),
          completed_at: now.toISOString(),
          duration_ms: executionTimeMs || 0
        },
        output: {
          stdout: outputData.type === 'success' ? outputData.message : '',
          stderr: outputData.type === 'error' ? outputData.message : '',
          exit_code: outputData.type === 'error' ? 1 : 0
        },
        artifacts: (outputData.artifacts || []).map(artifact => ({
          filename: artifact.filename,
          type: artifact.type,
          mime_type: artifact.mime_type,
          size_bytes: artifact.size
        }))
      };

      // Send to backend (fire and forget - don't wait)
      await codeEditorApi.logCodeExecution(logData, classContext);
      console.log('✅ Execution logged successfully');
    } catch (error) {
      // Don't fail the execution if logging fails
      console.warn('⚠️ Failed to log execution:', error);
    }
  };

  // Expose methods and state to parent component
  useImperativeHandle(ref, () => ({
    runCode: handleRunCode,
    stopExecution: () => {
      const fileName = activeFile?.file?.name || activeFile?.name;
      const isPython = fileName?.endsWith('.py');

      if (isPython && isExecutingPython) {
        cancelPython();
      } else if (!isPython && isExecutingOctave) {
        cancelOctave();
      }
    },
    get isExecuting() {
      return isExecutingPython || isExecutingOctave;
    }
  }));

  const handleRunCode = async () => {
    if (!activeFile || !code.trim()) {
      setCurrentOutput({
        type: 'error',
        message: 'No active file or code to execute',
        timestamp: new Date().toISOString()
      });
      return;
    }

    // Detect language from file extension
    const fileExt = activeFile?.file?.name?.split('.').pop()?.toLowerCase();
    const isPython = fileExt === 'py';
    const isMatlab = fileExt === 'm';

    if (!isPython && !isMatlab) {
      setCurrentOutput({
        type: 'error',
        message: `Unsupported file type: .${fileExt}. Only .m (MATLAB) and .py (Python) files can be executed.`,
        timestamp: new Date().toISOString()
      });
      return;
    }

    if (isPython && !pythonReady) {
      setCurrentOutput({
        type: 'error',
        message: 'Python is still loading... Please wait.',
        timestamp: new Date().toISOString()
      });
      return;
    }

    if (isMatlab && !octaveReady) {
      setCurrentOutput({
        type: 'error',
        message: 'Octave is still loading... Please wait.',
        timestamp: new Date().toISOString()
      });
      return;
    }

    // Clear previous output and artifacts to prevent caching
    setCurrentOutput(null);
    setActiveTab('current');

    let stdout = '';
    let stderr = '';
    let artifacts = [];

    // Initialize output with "executing" state
    setCurrentOutput({
      type: 'executing',
      message: '',
      timestamp: new Date().toISOString(),
      fileName: activeFile.file?.name || activeFile.name,
      language: isPython ? 'Python' : 'MATLAB',
      executionTime: '0.00s',
      artifacts: []
    });

    try {
      // Select execution function based on language
      const executeFunction = isPython ? executePython : executeOctave;
      const languageName = isPython ? 'Python' : 'MATLAB';

      executeFunction(code, {
        onStdout: (data) => {
          stdout += data + '\n';
          // Update output gradually as stdout comes in
          setCurrentOutput(prev => ({
            ...prev,
            type: 'executing',
            message: stdout
          }));
        },
        onStderr: (data) => {
          stderr += data + '\n';
          // Update output gradually as stderr comes in
          setCurrentOutput(prev => ({
            ...prev,
            type: 'executing',
            message: stderr
          }));
        },
        onArtifacts: (data) => {
          // Merge with existing streaming plots
          artifacts = [...artifacts, ...data];
        },
        onPlotStream: (plotData) => {
          // Handle streaming plots - append them incrementally
          artifacts = [...artifacts, ...plotData];

          // Update current output with new plots immediately
          setCurrentOutput(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              artifacts: [...(prev.artifacts || []), ...plotData]
            };
          });
        },
        onComplete: (result, executionTimeMs) => {
          // Use execution time from worker (measured at actual execution)
          const executionTime = executionTimeMs
            ? (executionTimeMs / 1000).toFixed(2) + 's'
            : '0.00s';
          // Use stdout collected from onStdout callbacks
          // Don't use result as it's an object { exitCode: 0 }, not a string
          const output = stdout || '';
          const hasError = stderr.length > 0;

          const outputData = {
            type: hasError ? 'error' : 'success',
            message: hasError ? stderr : output,
            timestamp: new Date().toISOString(),
            fileName: activeFile.file?.name || activeFile.name,
            language: languageName,
            executionTime,
            artifacts: artifacts // Include artifacts in output
          };

          setCurrentOutput(outputData);

          // Add to execution history - only if code is different from last execution
          setExecutionHistory(prev => {
            // Check if code is identical to the most recent history item
            if (prev.length > 0) {
              const lastHistoryItem = prev[0];
              // Compare the full code, not just the truncated preview
              const lastFullCode = lastHistoryItem.fullCode || lastHistoryItem.code;
              if (lastFullCode === code || (lastHistoryItem.code === code.substring(0, 100) + (code.length > 100 ? '...' : ''))) {
                // Code hasn't changed, don't add duplicate
                return prev;
              }
            }

            // Code is different or this is first execution, add to history
            return [{
              id: Date.now(),
              fileName: activeFile.file?.name || activeFile.name,
              language: languageName,
              code: code.substring(0, 100) + (code.length > 100 ? '...' : ''),
              fullCode: code, // Store full code for comparison
              output: outputData.message,
              type: outputData.type,
              timestamp: outputData.timestamp,
              executionTime
            }, ...prev.slice(0, 9)]; // Keep only last 10 executions
          });

          // Send output to backend for logging
          sendExecutionLog(outputData, executionTimeMs);
        },
        onError: (error, executionTimeMs) => {
          // Use execution time from worker (measured at actual execution)
          const executionTime = executionTimeMs
            ? (executionTimeMs / 1000).toFixed(2) + 's'
            : '0.00s';

          setCurrentOutput({
            type: 'error',
            message: `Execution failed: ${error}`,
            timestamp: new Date().toISOString(),
            fileName: activeFile.file?.name || activeFile.name,
            language: language,
            executionTime
          });
        }
      });
    } catch (error) {
      setCurrentOutput({
        type: 'error',
        message: `Execution failed: ${error.message}`,
        timestamp: new Date().toISOString()
      });
    }
  };

  const clearOutput = () => {
    setCurrentOutput(null);
  };

  const clearHistory = () => {
    setExecutionHistory([]);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString() + ' - ' + date.toLocaleDateString();
  };

  if (!showOutput) return null;

  return (
    <div className={`${placement === 'right' ? 'border-l' : 'border-t'} border-gray-200 bg-white flex flex-col h-full overflow-hidden`}>
      {/* Header */}
      <div className="h-12 bg-white border-b border-gray-200/60 flex items-center justify-between px-4 flex-shrink-0">
        <div className="flex items-center space-x-4 flex-1 min-w-0">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-gray-800">Output</h3>
          </div>

          {/* Tab Navigation */}
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('current')}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors duration-200 ${
                activeTab === 'current'
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              Current
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors duration-200 ${
                activeTab === 'history'
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              History ({executionHistory.length})
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center space-x-3 flex-shrink-0">
          <button
            onClick={activeTab === 'current' ? clearOutput : clearHistory}
            className="px-3 py-2 text-sm rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-colors duration-200"
          >
            Clear
          </button>

          <button
            onClick={onToggleOutput}
            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors duration-200"
            title="Hide Output Pane"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'current' ? (
          // Current Output Tab
          <div>
            {currentOutput ? (
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className={`inline-block w-2 h-2 rounded-full ${
                      currentOutput.type === 'success' ? 'bg-green-500' :
                      currentOutput.type === 'error' ? 'bg-red-500' :
                      'bg-yellow-500 animate-pulse'
                    }`}></span>
                    <span className="text-sm font-medium text-gray-700">
                      {currentOutput.fileName} ({currentOutput.language})
                      {currentOutput.type === 'executing' && <span className="ml-2 text-xs text-gray-500">Running...</span>}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatTimestamp(currentOutput.timestamp)} • {currentOutput.executionTime}
                  </div>
                </div>
                <div className={`bg-gray-900 text-green-400 p-3 rounded font-mono text-sm whitespace-pre-wrap ${
                  currentOutput.type === 'error' ? 'text-red-400' : ''
                }`}>
                  {currentOutput.message || (currentOutput.type === 'executing' ? 'Executing code...' : 'No output yet')}
                </div>

                {/* Display plots if any artifacts are present */}
                {currentOutput.artifacts && currentOutput.artifacts.filter(a => a.type === 'plot').length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      Generated Plots ({currentOutput.artifacts.filter(a => a.type === 'plot').length})
                    </h4>
                    <div className="space-y-3">
                      {currentOutput.artifacts.filter(a => a.type === 'plot').map((plot, idx) => {
                        // Helper function to download SVG as PDF (matching ERD editor style)
                        const downloadAsPDF = (svgElement, filename) => {
                          if (!svgElement) return;

                          try {
                            const today = new Date();
                            const svgData = new XMLSerializer().serializeToString(svgElement);

                            const htmlContent = `
                              <!DOCTYPE html>
                              <html>
                              <head>
                                <title>${filename}</title>
                                <style>
                                  body {
                                    margin: 0;
                                    padding: 20px;
                                    font-family: Arial, sans-serif;
                                    background: white;
                                  }
                                  .header {
                                    text-align: center;
                                    margin-bottom: 20px;
                                    border-bottom: 2px solid #333;
                                    padding-bottom: 10px;
                                  }
                                  .header h1 {
                                    margin: 0 0 10px 0;
                                    font-size: 24px;
                                  }
                                  .header p {
                                    margin: 5px 0;
                                    color: #666;
                                  }
                                  .diagram-container {
                                    display: flex;
                                    justify-content: center;
                                    align-items: center;
                                    width: 100%;
                                    min-height: 400px;
                                    user-select: none;
                                  }
                                  .diagram-container svg {
                                    max-width: 100%;
                                    height: auto;
                                    user-select: none;
                                  }
                                  .footer {
                                    margin-top: 20px;
                                    text-align: center;
                                    font-size: 12px;
                                    color: #666;
                                    border-top: 1px solid #ccc;
                                    padding-top: 10px;
                                  }
                                  @media print {
                                    body { margin: 0; }
                                    .no-print { display: none; }
                                  }
                                </style>
                              </head>
                              <body>
                                <div class="header">
                                  <h1>MATLAB Plot</h1>
                                  <p>User: ${username} | Date: ${today.toLocaleDateString()}</p>
                                </div>
                                <div class="diagram-container">
                                  ${svgData}
                                </div>
                                <div class="footer">
                                  <p>Generated on ${today.toLocaleString()}</p>
                                </div>
                              </body>
                              </html>
                            `;

                            const printWindow = window.open('', '_blank');
                            printWindow.document.write(htmlContent);
                            printWindow.document.close();

                            printWindow.onload = () => {
                              setTimeout(() => {
                                printWindow.print();
                              }, 500);
                            };
                          } catch (error) {
                            console.error('Error generating PDF:', error);
                            alert('Error generating PDF. Please try again.');
                          }
                        };
                        // Check if this is a JSON plot or an image plot
                        const isJsonPlot = plot.mime_type === 'application/json';

                        if (isJsonPlot) {
                          // Parse JSON plot data and render with SVG
                          try {
                            const jsonData = JSON.parse(new TextDecoder().decode(plot.data));
                            const plotType = jsonData.type || 'plot';

                            // Handle 3D plots
                            if (plotType === 'plot3d' || plotType === 'surf') {
                              return render3DPlot(plot, jsonData, idx, currentOutput, username);
                            }

                            // Handle heatmap plots
                            if (plotType === 'heatmap') {
                              const { rows, cols, data, colormap: cmap, title: heatmapTitle } = jsonData;
                              const width = 600;
                              const height = 500;
                              const padding = 60;
                              const cellWidth = (width - 2 * padding) / cols;
                              const cellHeight = (height - 2 * padding) / rows;

                              // Find min/max for color scaling
                              const minVal = Math.min(...data);
                              const maxVal = Math.max(...data);
                              const range = maxVal - minVal || 1;

                              // Color interpolation based on colormap
                              const getColor = (value) => {
                                const normalized = (value - minVal) / range;
                                if (cmap === 'hot') {
                                  // Hot colormap: black -> red -> yellow -> white
                                  if (normalized < 0.33) {
                                    const t = normalized / 0.33;
                                    return `rgb(${Math.round(255 * t)}, 0, 0)`;
                                  } else if (normalized < 0.66) {
                                    const t = (normalized - 0.33) / 0.33;
                                    return `rgb(255, ${Math.round(255 * t)}, 0)`;
                                  } else {
                                    const t = (normalized - 0.66) / 0.34;
                                    return `rgb(255, 255, ${Math.round(255 * t)})`;
                                  }
                                } else {
                                  // Default: blue to red
                                  const r = Math.round(255 * normalized);
                                  const b = Math.round(255 * (1 - normalized));
                                  return `rgb(${r}, 0, ${b})`;
                                }
                              };

                              return (
                                <div key={idx} className="bg-white border rounded p-4 w-full overflow-hidden">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-medium text-gray-600">Heatmap {idx + 1}</span>
                                    <button
                                      onClick={() => {
                                        const svgElement = document.getElementById(`heatmap-svg-${idx}`);
                                        if (svgElement) {
                                          downloadAsPDF(svgElement, `heatmap_${idx + 1}`);
                                        }
                                      }}
                                      className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                    >
                                      Download PDF
                                    </button>
                                  </div>
                                  <div className="bg-gray-50 p-4 rounded w-full overflow-hidden">
                                    <svg
                                      id={`heatmap-svg-${idx}`}
                                      viewBox={`0 0 ${width} ${height}`}
                                      preserveAspectRatio="xMidYMid meet"
                                      className="w-full h-auto max-w-full"
                                      style={{ maxHeight: '500px' }}
                                    >
                                      {/* Title */}
                                      {heatmapTitle && (
                                        <text x={width / 2} y={25} textAnchor="middle" fontSize="16" fontWeight="bold" fill="#1f2937">
                                          {heatmapTitle}
                                        </text>
                                      )}

                                      {/* Heatmap cells */}
                                      {data.map((value, i) => {
                                        const row = Math.floor(i / cols);
                                        const col = i % cols;
                                        const x = padding + col * cellWidth;
                                        const y = padding + row * cellHeight;
                                        return (
                                          <rect
                                            key={i}
                                            x={x}
                                            y={y}
                                            width={cellWidth}
                                            height={cellHeight}
                                            fill={getColor(value)}
                                            stroke="#fff"
                                            strokeWidth="0.5"
                                          />
                                        );
                                      })}

                                      {/* Colorbar */}
                                      <g transform={`translate(${width - padding + 10}, ${padding})`}>
                                        {Array.from({ length: 50 }, (_, i) => {
                                          const value = minVal + (i / 50) * range;
                                          const y = (height - 2 * padding) * (1 - i / 50);
                                          return (
                                            <rect
                                              key={i}
                                              x={0}
                                              y={y}
                                              width={20}
                                              height={(height - 2 * padding) / 50}
                                              fill={getColor(value)}
                                            />
                                          );
                                        })}
                                        <text x={25} y={0} fontSize="10" fill="#374151">{maxVal.toFixed(1)}</text>
                                        <text x={25} y={height - 2 * padding} fontSize="10" fill="#374151">{minVal.toFixed(1)}</text>
                                      </g>
                                    </svg>
                                  </div>
                                </div>
                              );
                            }

                            // Handle 2D plots
                            const { x, y } = jsonData;

                            // Calculate SVG dimensions and scale
                            const width = 600;
                            const height = 400;
                            const padding = 50;
                            const plotWidth = width - 2 * padding;
                            const plotHeight = height - 2 * padding;

                            const xMin = Math.min(...x);
                            const xMax = Math.max(...x);
                            const yMin = Math.min(...y);
                            const yMax = Math.max(...y);

                            const xScale = (val) => padding + ((val - xMin) / (xMax - xMin)) * plotWidth;
                            const yScale = (val) => height - padding - ((val - yMin) / (yMax - yMin)) * plotHeight;

                            // Generate path
                            const pathData = x.map((xVal, i) => {
                              const command = i === 0 ? 'M' : 'L';
                              return `${command} ${xScale(xVal)} ${yScale(y[i])}`;
                            }).join(' ');

                            // Extract plot number from filename (e.g., "plot_001.json" -> "001")
                            const plotNumber = plot.filename.match(/plot_(\d+)\.json/)?.[1];

                            // Get plot metadata if available (matched by plot number)
                            const titleArtifact = currentOutput.artifacts.find(a => a.filename === `plot_${plotNumber}_title.txt`);
                            const xlabelArtifact = currentOutput.artifacts.find(a => a.filename === `plot_${plotNumber}_xlabel.txt`);
                            const ylabelArtifact = currentOutput.artifacts.find(a => a.filename === `plot_${plotNumber}_ylabel.txt`);

                            const title = titleArtifact ? new TextDecoder().decode(titleArtifact.data) : '';
                            const xlabel = xlabelArtifact ? new TextDecoder().decode(xlabelArtifact.data) : '';
                            const ylabel = ylabelArtifact ? new TextDecoder().decode(ylabelArtifact.data) : '';

                            return (
                              <div key={idx} className="bg-white border rounded p-4 w-full overflow-hidden">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-medium text-gray-600">Plot {idx + 1}</span>
                                  <button
                                    onClick={() => {
                                      const svgElement = document.getElementById(`plot-svg-${idx}`);
                                      if (svgElement) {
                                        downloadAsPDF(svgElement, `plot_${idx + 1}.svg`);
                                      }
                                    }}
                                    className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                  >
                                    Download PDF
                                  </button>
                                </div>
                                <div className="bg-gray-50 p-4 rounded w-full overflow-hidden" style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}>
                                  <svg
                                    id={`plot-svg-${idx}`}
                                    viewBox={`0 0 ${width} ${height}`}
                                    preserveAspectRatio="xMidYMid meet"
                                    className="w-full h-auto max-w-full"
                                    style={{ maxHeight: '400px', userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none', cursor: 'grab' }}
                                  >
                                    {/* Grid lines */}
                                    <defs>
                                      <pattern id={`grid-${idx}`} width="40" height="40" patternUnits="userSpaceOnUse">
                                        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e5e7eb" strokeWidth="0.5"/>
                                      </pattern>
                                    </defs>
                                    <rect x={padding} y={padding} width={plotWidth} height={plotHeight} fill={`url(#grid-${idx})`} />

                                    {/* Axes */}
                                    <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#374151" strokeWidth="2" />
                                    <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#374151" strokeWidth="2" />

                                    {/* Title */}
                                    {title && (
                                      <text x={width / 2} y={25} textAnchor="middle" fontSize="16" fontWeight="bold" fill="#1f2937">
                                        {title}
                                      </text>
                                    )}

                                    {/* X-axis label */}
                                    {xlabel && (
                                      <text x={width / 2} y={height - 10} textAnchor="middle" fontSize="12" fill="#4b5563">
                                        {xlabel}
                                      </text>
                                    )}

                                    {/* Y-axis label */}
                                    {ylabel && (
                                      <text x={20} y={height / 2} textAnchor="middle" fontSize="12" fill="#4b5563" transform={`rotate(-90, 20, ${height / 2})`}>
                                        {ylabel}
                                      </text>
                                    )}

                                    {/* Axis labels */}
                                    <text x={width - padding} y={height - padding + 20} textAnchor="end" fontSize="10" fill="#6b7280">
                                      {xMax.toFixed(2)}
                                    </text>
                                    <text x={padding} y={height - padding + 20} textAnchor="start" fontSize="10" fill="#6b7280">
                                      {xMin.toFixed(2)}
                                    </text>
                                    <text x={padding - 10} y={padding} textAnchor="end" fontSize="10" fill="#6b7280">
                                      {yMax.toFixed(2)}
                                    </text>
                                    <text x={padding - 10} y={height - padding} textAnchor="end" fontSize="10" fill="#6b7280">
                                      {yMin.toFixed(2)}
                                    </text>

                                    {/* Plot line */}
                                    <path d={pathData} fill="none" stroke="#3b82f6" strokeWidth="2" />

                                    {/* Data points */}
                                    {x.map((xVal, i) => (
                                      <circle
                                        key={i}
                                        cx={xScale(xVal)}
                                        cy={yScale(y[i])}
                                        r="3"
                                        fill="#3b82f6"
                                        stroke="#1e40af"
                                        strokeWidth="1"
                                      />
                                    ))}
                                  </svg>
                                </div>
                              </div>
                            );
                          } catch (err) {
                            console.error('Failed to parse plot JSON:', err);
                            return (
                              <div key={idx} className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">
                                Failed to render plot: {plot.filename}
                              </div>
                            );
                          }
                        } else {
                          // Image plot (PNG, JPG, SVG)
                          // Special handling for SVG - decode as text and inject directly
                          const isSvg = plot.mime_type === 'image/svg+xml';

                          return (
                            <div key={idx} className="bg-white border rounded p-2">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-gray-600">{plot.filename}</span>
                                <button
                                  onClick={() => {
                                    // Convert Uint8Array to blob and download
                                    const blob = new Blob([plot.data], { type: plot.mime_type });
                                    const url = URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.download = plot.filename;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    URL.revokeObjectURL(url);
                                  }}
                                  className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                >
                                  Download
                                </button>
                              </div>
                              {isSvg ? (
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: new TextDecoder().decode(plot.data)
                                  }}
                                  className="max-w-full"
                                  style={{ maxHeight: '400px', overflow: 'auto' }}
                                />
                              ) : (
                                <img
                                  src={`data:${plot.mime_type};base64,${btoa(String.fromCharCode(...new Uint8Array(plot.data)))}`}
                                  alt={plot.filename}
                                  className="max-w-full h-auto border rounded"
                                  style={{ maxHeight: '400px' }}
                                />
                              )}
                            </div>
                          );
                        }
                      })}
                    </div>
                  </div>
                )}

                {/* Display other file artifacts if any (excluding plot metadata) */}
                {currentOutput.artifacts && currentOutput.artifacts.filter(a => a.type !== 'plot' && a.type !== 'plot-meta').length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      Output Files ({currentOutput.artifacts.filter(a => a.type !== 'plot' && a.type !== 'plot-meta').length})
                    </h4>
                    <div className="space-y-2">
                      {currentOutput.artifacts.filter(a => a.type !== 'plot' && a.type !== 'plot-meta').map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-500">📄</span>
                            <div>
                              <div className="text-sm font-medium text-gray-700">{file.filename}</div>
                              <div className="text-xs text-gray-500">
                                {(file.size / 1024).toFixed(1)} KB • {file.mime_type}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              const blob = new Blob([file.data], { type: file.mime_type });
                              const url = URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = file.filename;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              URL.revokeObjectURL(url);
                            }}
                            className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                          >
                            Download
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center min-h-full text-gray-500">
                <div className="text-center">
                  <div className="text-2xl mb-2">▶</div>
                  <div className="text-sm">Click "Run" to execute your code</div>
                  <div className="text-xs mt-1">Output will appear here</div>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Execution History Tab
          <div>
            {executionHistory.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {executionHistory.map((execution) => (
                  <div key={execution.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${
                          execution.type === 'success' ? 'bg-green-500' : 'bg-red-500'
                        }`}></span>
                        <span className="text-sm font-medium text-gray-700">
                          {execution.fileName} ({execution.language})
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatTimestamp(execution.timestamp)} • {execution.executionTime}
                      </div>
                    </div>

                    {/* Code Preview */}
                    <div className="bg-gray-100 p-2 rounded text-xs font-mono text-gray-600 mb-2">
                      {execution.code}
                    </div>

                    {/* Output Preview */}
                    <div className={`bg-gray-900 text-green-400 p-2 rounded font-mono text-xs ${
                      execution.type === 'error' ? 'text-red-400' : ''
                    }`}>
                      {execution.output.length > 200
                        ? execution.output.substring(0, 200) + '...'
                        : execution.output
                      }
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center min-h-full text-gray-500">
                <div className="text-center">
                  <div className="text-2xl mb-2">📋</div>
                  <div className="text-sm">No execution history</div>
                  <div className="text-xs mt-1">Previous runs will appear here</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

OutputPane.displayName = 'OutputPane';

export default OutputPane;